// @ts-nocheck
import { useState, useMemo, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { ClipboardPenLine, Droplet, Image as ImageIcon, SlidersHorizontal, Snowflake, type LucideIcon } from "lucide-react";
import {
  Form, Input, InputNumber, Switch, Select, Button, Collapse, App, Tag, Checkbox,
} from "../components/admin-ui";
import {
  LeftOutlined, PlusOutlined, CloudUploadOutlined, CheckCircleFilled, DeleteOutlined,
} from "../components/admin-icons";
import { useNavigate, useLocation } from "react-router-dom";
import type { Stall } from "@kawkaw/shared";
import { AdminShell } from "../layouts/AdminShell";
import { ImageUpload } from "../components/ImageUpload";
import type { MenuItem } from "@kawkaw/shared";
import {
  useAdminStalls,
  useAdminMenuItems,
  useAdminMenuCategories,
  useCreateMenuItem,
  useUpdateMenuItem,
  useCreateOptionGroup,
  useUpdateOptionGroup,
  useDeleteOptionGroup,
  useCreateOptionChoice,
  useUpdateOptionChoice,
  useDeleteOptionChoice,
  type MenuCategoryRow,
} from "../api/hooks";

/* ─── Customization option group icons ─────────────────── */
const GROUP_ICON_DEFS: { key: string; Icon: LucideIcon }[] = [
  { key: "sugar", Icon: Droplet },
  { key: "ice", Icon: Snowflake },
  { key: "special", Icon: ClipboardPenLine },
];

const GROUP_ICON_LABEL_KEY: Record<string, string> = {
  sugar: "addMenuItem.groupIconSugar",
  ice: "addMenuItem.groupIconIce",
  special: "addMenuItem.groupIconSpecial",
};

/** A required group always needs at least one pick — same rule the server and the customer app apply. Used on load, on edit and on save so min can't stay 0 while "required" is on. */
const effectiveMin = (required: boolean, min: number) => (required ? Math.max(1, min || 0) : (min || 0));

function GroupIcon({ iconKey, size = 16, color = "currentColor" }: { iconKey: string; size?: number; color?: string }) {
  const match = GROUP_ICON_DEFS.find(i => i.key === iconKey);
  if (!match) return null;
  const Icon = match.Icon;
  return <Icon size={size} color={color} strokeWidth={2.4} style={{ display: "block" }} aria-hidden="true" />;
}

/* ─── Local types for inline customization ──────────────── */
type LocalChoice = {
  localId: string;
  id?: string; // set when hydrated from an existing choice — PATCH instead of POST on save
  label: string;
  extra_charge: number;
  isDefault: boolean;
  input_type: "radio" | "quantity" | "input" | "textarea";
};

type LocalGroup = {
  localId: string;
  id?: string; // set when hydrated from an existing group — PATCH instead of POST on save
  name: string;
  icon_key?: string;      // "sugar" | "ice" | "special"
  categoryGroupId?: string; // category default group this mirrors; backend keeps it synced
  required: boolean;
  selection: "single" | "multiple";
  min_select: number;
  max_select: number;
  choices: LocalChoice[];
};

/* ─── Section header ────────────────────────────────────── */
function SectionHead({ num, label }: { num: string; label: string }) {
  return (
    <div className="admin-editor-section-heading" style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
      <span className="admin-editor-section-number" style={{
        width: 26, height: 26, borderRadius: "50%",
        background: "#F97316", color: "#fff",
        fontSize: 12, fontWeight: 800,
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0,
      }}>{num}</span>
      <h2 className="admin-editor-section-title">{label}</h2>
    </div>
  );
}

/* ─── Customer Preview card ──────────────────────────────── */
function PreviewCard({
  name, price, imageUrl, isAvailable, description,
}: { name?: string; price?: number; imageUrl?: string; isAvailable?: boolean; description?: string }) {
  const { t } = useTranslation();
  const available = isAvailable !== false;
  return (
    <div className="admin-editor-preview-card" style={{ borderRadius: 16, border: "1px solid #E5E7EB", overflow: "hidden", background: "#fff" }}>
      {/* Image area */}
      <div className="admin-editor-preview-image" style={{
        position: "relative", height: 160,
        background: imageUrl ? undefined : "#2D2D2D",
        display: "flex", alignItems: "flex-end", justifyContent: "space-between",
        padding: "8px 12px",
      }}>
        {imageUrl && (
          <img
            src={imageUrl} alt=""
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
          />
        )}
        {!imageUrl && <span className="admin-editor-preview-placeholder" aria-hidden="true"><ImageIcon size={36} strokeWidth={1.5} /></span>}
        <span className="admin-editor-preview-status" style={{
          position: "relative",
          background: available ? "#16A34A" : "#9E9E9E",
          color: "#fff", fontSize: 10, fontWeight: 800, letterSpacing: 0.5,
          borderRadius: 20, padding: "3px 10px",
        }}>
          {available ? t("addMenuItem.badgeAvailable") : t("addMenuItem.badgeUnavailable")}
        </span>
        <span className="admin-editor-preview-price" style={{
          position: "relative",
          background: "#F97316", color: "#fff",
          fontSize: 14, fontWeight: 800,
          borderRadius: 8, padding: "3px 10px",
        }}>
          RM {price != null ? Number(price).toFixed(2) : "0.00"}
        </span>
      </div>

      {/* Info */}
      <div className="admin-editor-preview-info" style={{ padding: "14px 16px" }}>
        <div className="admin-editor-preview-name" style={{ fontSize: 15, fontWeight: 800, color: name ? "#212121" : "#D1D5DB", marginBottom: 6 }}>
          {name || t("addMenuItem.previewNameFallback")}
        </div>
        <div className="admin-editor-preview-description" style={{ fontSize: 12, color: description?.trim() ? "#6B7280" : "#9E9E9E", marginBottom: 14, lineHeight: 1.5, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
          {description?.trim() || t("addMenuItem.previewDescHint")}
        </div>
        <div className="admin-editor-preview-customize" style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          paddingTop: 10, borderTop: "1px solid #F3F4F6",
        }}>
          <span style={{ fontSize: 13, color: "#616161" }}>{t("addMenuItem.customizeYourOrder")}</span>
          <span style={{ color: "#9E9E9E", fontSize: 16 }}>›</span>
        </div>
      </div>
    </div>
  );
}

/* ─── Main screen ────────────────────────────────────────── */
export function AddMenuItemScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { message: msg } = App.useApp();

  const selectionOptions = useMemo(
    () => [
      { value: "single" as const, label: t("addMenuItem.selectionSingle") },
      { value: "multiple" as const, label: t("addMenuItem.selectionMultiple") },
    ],
    [t],
  );
  const inputTypeOptions = useMemo(
    () => [
      { value: "radio" as const, label: t("addMenuItem.typeRadio") },
      { value: "quantity" as const, label: t("addMenuItem.typeQuantity") },
      { value: "input" as const, label: t("addMenuItem.typeInput") },
      { value: "textarea" as const, label: t("addMenuItem.typeTextarea") },
    ],
    [t],
  );

  const navState = location.state as { stallId?: string; item?: MenuItem } | null;
  const editingItem = navState?.item;
  const isEdit = !!editingItem;
  const [selectedStallId, setSelectedStallId] = useState<string | undefined>(editingItem?.stall_id ?? navState?.stallId);

  const [form] = Form.useForm();
  const createItem = useCreateMenuItem();
  const updateItem = useUpdateMenuItem();
  const createGroup = useCreateOptionGroup();
  const updateGroupApi = useUpdateOptionGroup();
  const deleteGroupApi = useDeleteOptionGroup();
  const createChoice = useCreateOptionChoice();
  const updateChoiceApi = useUpdateOptionChoice();
  const deleteChoiceApi = useDeleteOptionChoice();

  const [custEnabled, setCustEnabled] = useState(() => !!editingItem?.option_groups?.length);
  const [groups, setGroups] = useState<LocalGroup[]>(() => (editingItem?.option_groups ?? []).map(g => ({
    localId: g.id,
    id: g.id,
    name: g.name,
    icon_key: g.icon_key ?? undefined,
    required: g.type === "required",
    selection: g.max_select > 1 ? "multiple" as const : "single" as const,
    min_select: effectiveMin(g.type === "required", g.min_select),
    max_select: g.max_select,
    choices: (g.choices ?? []).map(c => ({
      localId: c.id,
      id: c.id,
      label: c.label,
      extra_charge: Number(c.extra_charge),
      isDefault: false,
      input_type: c.input_type ?? "radio",
    })),
  })));
  // Groups/choices present on the item when the screen opened — diffed
  // against `groups` on save to know what to update/create/delete, instead
  // of naively recreating everything (which would drop real backend ids and
  // spam duplicate rows on every save).
  const originalGroupsRef = useRef(editingItem?.option_groups ?? []);

  // The admin GET /admin/menu/items list includes a `categories` relation
  // array, not the flat `category_ids` the shared MenuItem type declares —
  // same mismatch Menu.tsx's own edit modal already worked around.
  const editFormInitialValues = useMemo(() => {
    if (!editingItem) return { is_available: true, is_featured: false, sort_order: 0 };
    const row = editingItem as MenuItem & { categories?: Array<{ id: string }> };
    return {
      name: row.name,
      description: row.description ?? undefined,
      price: row.price != null ? Number(row.price) : undefined,
      image_url: row.image_url ?? undefined,
      image_url_wide: row.image_url_wide ?? undefined,
      is_available: row.is_available,
      is_featured: row.is_featured ?? false,
      sort_order: row.sort_order ?? 0,
      category_ids: row.categories?.map(c => c.id) ?? row.category_ids ?? [],
      available_from: row.available_from ?? undefined,
      available_to: row.available_to ?? undefined,
      daily_stock: row.daily_stock ?? undefined,
      offer_price: row.offer_price != null ? Number(row.offer_price) : undefined,
      offer_from: row.offer_from ?? undefined,
      offer_to: row.offer_to ?? undefined,
    };
    // editingItem is a static snapshot from navigation state — never changes
    // for the lifetime of this screen, so this only needs to compute once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const newUid = () => Math.random().toString(36).slice(2, 10);
  const addGroup = () => setGroups(g => [...g, {
    localId: newUid(), name: "", icon_key: undefined, required: false, selection: "single" as const, min_select: 0, max_select: 1, choices: [],
  }]);
  const updateGroup = (id: string, patch: Partial<LocalGroup>) =>
    setGroups(g => g.map(x => x.localId === id ? { ...x, ...patch } : x));
  const removeGroup = (id: string) => setGroups(g => g.filter(x => x.localId !== id));
  const addChoice = (groupId: string) =>
    setGroups(g => g.map(x => x.localId !== groupId ? x : {
      ...x, choices: [...x.choices, { localId: newUid(), label: "", extra_charge: 0, isDefault: false, input_type: "radio" as const }],
    }));
  const updateChoice = (groupId: string, choiceId: string, patch: Partial<LocalChoice>) =>
    setGroups(g => g.map(x => x.localId !== groupId ? x : {
      ...x, choices: x.choices.map(c => c.localId === choiceId ? { ...c, ...patch } : c),
    }));
  const removeChoice = (groupId: string, choiceId: string) =>
    setGroups(g => g.map(x => x.localId !== groupId ? x : {
      ...x, choices: x.choices.filter(c => c.localId !== choiceId),
    }));

  const { data: stallItemsRes } = useAdminMenuItems(selectedStallId);
  // Option groups already built on this stall's other dishes, de-duplicated by
  // name + choices + prices, so a group can be copied into this item (the copy
  // is independent — editing it never touches the source dish).
  const existingGroups = useMemo(() => {
    const seen = new Map<string, { key: string; label: string; group: NonNullable<MenuItem["option_groups"]>[number] }>();
    for (const it of stallItemsRes?.data ?? []) {
      if (editingItem && it.id === editingItem.id) continue;
      for (const g of it.option_groups ?? []) {
        const key = JSON.stringify([g.name, g.type, g.min_select, g.max_select, (g.choices ?? []).map(c => [c.label, Number(c.extra_charge)])]);
        if (!seen.has(key)) seen.set(key, { key, label: `${g.name} (${(g.choices ?? []).map(c => Number(c.extra_charge) > 0 ? `${c.label} +${Number(c.extra_charge).toFixed(2)}` : c.label).join(" / ")}) · ${it.name}`, group: g });
      }
    }
    return [...seen.values()];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stallItemsRes]);
  const copyExistingGroup = (key: string) => {
    const src = existingGroups.find(x => x.key === key)?.group;
    if (!src) return;
    setCustEnabled(true);
    setGroups(g => [...g, {
      localId: newUid(), name: src.name, icon_key: src.icon_key ?? undefined,
      required: src.type === "required",
      selection: src.max_select > 1 ? "multiple" as const : "single" as const,
      min_select: effectiveMin(src.type === "required", src.min_select), max_select: src.max_select,
      choices: (src.choices ?? []).map(c => ({ localId: newUid(), label: c.label, extra_charge: Number(c.extra_charge), isDefault: false, input_type: c.input_type ?? "radio" })),
    }]);
  };
  const pickExistingNode = existingGroups.length > 0 ? (
    <Select
      style={{ width: "100%", marginBottom: 10 }}
      placeholder={t("addMenuItem.copyExistingGroup")}
      value={undefined}
      onChange={(v: string) => copyExistingGroup(v)}
      options={existingGroups.map(x => ({ value: x.key, label: x.label }))}
    />
  ) : null;

  const { data: stallsRes } = useAdminStalls();
  const stalls: Stall[] = stallsRes?.data ?? [];
  const selectedStall = stalls.find(s => s.id === selectedStallId) ?? null;

  const { data: catRes } = useAdminMenuCategories(selectedStallId);
  const categories: MenuCategoryRow[] = catRes?.data ?? [];

  // Default to the first category once the newly-selected stall's categories
  // load — otherwise category_ids stays empty forever after the stall-change
  // handler resets it, and the field silently has no effect. Skipped once
  // the item already carries real category_ids (edit mode, seeded via the
  // Form's initialValues below), so this never clobbers the item's actual
  // categories with "just pick the first one".
  useEffect(() => {
    if (categories.length > 0 && !form.getFieldValue("category_ids")?.length) {
      form.setFieldsValue({ category_ids: [categories[0].id] });
    }
  }, [categories, form]);

  const categoryIds = Form.useWatch("category_ids", { form, preserve: true }) as string[] | undefined;

  // Auto-populate sugar/ice customization groups from the selected
  // category's admin-configured defaults (Categories.tsx's 糖度/冰量选项),
  // so that data isn't just stored and never used. Only kicks in before the
  // admin has touched customization themselves, so it never clobbers a
  // manual edit — tracked by comparing the current groups against a
  // signature of what we last auto-set, rather than just "groups.length >
  // 0", so switching to a different category (which also lands in
  // groups.length > 0) still refreshes to that category's own defaults
  // instead of leaving the previous category's groups stuck on screen.
  const autoPopulatedSignatureRef = useRef<string | null>(null);
  // Edit mode shows what the item REALLY has. Without this, an item whose option groups were deleted (groups.length === 0) looked "untouched"
  // and got the category's defaults painted on screen as if saved — admin said "has Add On", the customer app (reading the DB) showed none.
  // Defaults are only offered when the admin actually switches the category.
  const initialCategoryRef = useRef<string | undefined>(editingItem ? (editFormInitialValues.category_ids ?? []).join(",") : undefined);
  useEffect(() => {
    // Presets from EVERY selected category, in selection order (not just the first one).
    const selectedCats = (categoryIds ?? []).map(id => categories.find(c => c.id === id)).filter((c): c is MenuCategoryRow => !!c);
    if (selectedCats.length === 0) return;
    if (isEdit && selectedCats.map(c => c.id).join(",") === initialCategoryRef.current) return;
    const currentSignature = JSON.stringify(groups.map(g => ({ icon_key: g.icon_key, choices: g.choices.map(c => c.label + "|" + c.extra_charge + "|" + c.input_type) })));
    const untouchedSinceAutoPopulate = groups.length === 0 || currentSignature === autoPopulatedSignatureRef.current;
    if (custEnabled && !untouchedSinceAutoPopulate) return;
    const toGroup = (name: string, choices: Array<string | { label: string; extra_charge?: number; input_type?: string }>, categoryGroupId?: string, maxSelect?: number): LocalGroup => ({
      localId: newUid(),
      name,
      categoryGroupId,
      // Sugar/ice-named groups keep their dedicated icon for visual continuity;
      // any other admin-defined group (spice level, toppings, etc) gets the
      // generic "special" icon since there's no bespoke icon for it.
      icon_key: /糖|sugar/i.test(name) ? "sugar" : /冰|ice/i.test(name) ? "ice" : "special",
      required: false,
      selection: (maxSelect ?? 1) > 1 || choices.some(c => typeof c !== "string" && c.input_type === "quantity") ? "multiple" : "single",
      min_select: 0,
      max_select: maxSelect ?? (choices.some(c => typeof c !== "string" && c.input_type === "quantity") ? choices.length : 1),
      choices: choices.map(c => { const o = typeof c === "string" ? { label: c } : c; return { localId: newUid(), label: o.label, extra_charge: Number(o.extra_charge) || 0, isDefault: false, input_type: o.input_type ?? "radio" }; }),
    });
    const seen = new Set<string>();
    const newGroups: LocalGroup[] = selectedCats
      .flatMap(cat => cat.default_option_groups ?? [])
      .filter(g => g.name?.trim() && g.choices?.length)
      .filter(g => { const k = g.name.trim() + "|" + JSON.stringify(g.choices); if (seen.has(k)) return false; seen.add(k); return true; }) // two categories with the same preset → one group
      .map(g => toGroup(g.name, g.choices, g.id, g.max_select));
    const newSignature = JSON.stringify(newGroups.map(g => ({ icon_key: g.icon_key, choices: g.choices.map(c => c.label + "|" + c.extra_charge + "|" + c.input_type) })));
    if (newSignature === currentSignature) return; // already showing this category's defaults
    setGroups(newGroups);
    setCustEnabled(newGroups.length > 0);
    autoPopulatedSignatureRef.current = newSignature;
  }, [categoryIds, categories]);

  /* live preview via Form.useWatch */
  const previewName = Form.useWatch("name", { form, preserve: true }) as string | undefined;
  const previewPrice = Form.useWatch("price", { form, preserve: true }) as number | undefined;
  const previewImage = Form.useWatch("image_url", { form, preserve: true }) as string | undefined;
  const previewDescription = Form.useWatch("description", { form, preserve: true }) as string | undefined;
  const isAvailable = Form.useWatch("is_available", { form, preserve: true }) as boolean | undefined;
  const isFeatured = Form.useWatch("is_featured", { form, preserve: true }) as boolean | undefined;

  /* required fields for "all mandatory fields completed" indicator */
  const mandatoryComplete = !!(previewName && selectedStallId && previewPrice != null && previewPrice >= 0);

  // Creates every group/choice fresh — used for brand-new items, and for
  // any group in edit mode that doesn't have a real backend id yet.
  const createGroupAndChoices = async (itemId: string, g: LocalGroup, sortOrder: number) => {
    const gResult = await createGroup.mutateAsync({
      itemId, name: g.name,
      type: g.required ? "required" : "optional",
      min_select: effectiveMin(g.required, g.min_select),
      max_select: Math.max(g.max_select, effectiveMin(g.required, g.min_select)),
      sort_order: sortOrder,
      icon_key: g.icon_key,
      category_group_id: g.categoryGroupId,
    }) as { data: { id: string } };
    const groupId = gResult?.data?.id;
    if (!groupId) return;
    let ci = 0;
    for (const c of g.choices) {
      if (c.label.trim()) {
        await createChoice.mutateAsync({ groupId, label: c.label, extra_charge: c.extra_charge, sort_order: ci, input_type: c.input_type });
      }
      ci++;
    }
  };

  // Edit mode: diff `groups` against the item's original option_groups
  // (captured at screen-open time) so saving updates/creates/deletes only
  // what actually changed, instead of blindly recreating everything and
  // losing the real backend ids + orphaning the old rows.
  const saveGroupsDiffed = async (itemId: string) => {
    const original = originalGroupsRef.current;
    const keptIds = new Set(groups.filter(g => g.id).map(g => g.id));
    for (const og of original) {
      if (!keptIds.has(og.id)) await deleteGroupApi.mutateAsync(og.id);
    }
    let gi = 0;
    for (const g of groups) {
      if (!g.name.trim()) { gi++; continue; }
      if (g.id) {
        await updateGroupApi.mutateAsync({
          id: g.id, name: g.name,
          type: g.required ? "required" : "optional",
          min_select: effectiveMin(g.required, g.min_select), max_select: Math.max(g.max_select, effectiveMin(g.required, g.min_select)),
          sort_order: gi, icon_key: g.icon_key ?? null,
        });
        const origGroup = original.find(o => o.id === g.id);
        const origChoices = origGroup?.choices ?? [];
        const keptChoiceIds = new Set(g.choices.filter(c => c.id).map(c => c.id));
        for (const oc of origChoices) {
          if (!keptChoiceIds.has(oc.id)) await deleteChoiceApi.mutateAsync(oc.id);
        }
        let ci = 0;
        for (const c of g.choices) {
          if (c.label.trim()) {
            if (c.id) {
              await updateChoiceApi.mutateAsync({ id: c.id, label: c.label, extra_charge: c.extra_charge, sort_order: ci, input_type: c.input_type });
            } else {
              await createChoice.mutateAsync({ groupId: g.id, label: c.label, extra_charge: c.extra_charge, sort_order: ci, input_type: c.input_type });
            }
          }
          ci++;
        }
      } else {
        await createGroupAndChoices(itemId, g, gi);
      }
      gi++;
    }
  };

  const doSave = async (values: Record<string, unknown>) => {
    let itemId: string | undefined;
    if (isEdit && editingItem) {
      // This screen manages the item's groups itself, so the server must not auto-sync category defaults too.
      await updateItem.mutateAsync({ id: editingItem.id, ...values, skip_default_groups: true });
      itemId = editingItem.id;
    } else {
      const result = await createItem.mutateAsync({ ...values, stall_id: selectedStallId, skip_default_groups: true }) as { data: { id: string } };
      itemId = result?.data?.id;
    }
    if (!itemId) return;

    if (isEdit) {
      // Customization was turned off entirely, or every group was removed
      // — either way, every original group must go.
      if (!custEnabled || groups.length === 0) {
        for (const og of originalGroupsRef.current) await deleteGroupApi.mutateAsync(og.id);
      } else {
        await saveGroupsDiffed(itemId);
      }
    } else if (custEnabled && groups.length > 0) {
      let gi = 0;
      for (const g of groups) {
        if (g.name.trim()) await createGroupAndChoices(itemId, g, gi);
        gi++;
      }
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      await doSave(values);
      void msg.success(isEdit ? t("addMenuItem.successUpdate") : t("addMenuItem.successCreate"));
      navigate(-1);
    } catch {
      void msg.error(isEdit ? t("addMenuItem.failedUpdate") : t("addMenuItem.failedCreate"));
    }
  };

  const handleSaveAndAddAnother = async () => {
    try {
      const values = await form.validateFields();
      await doSave(values);
      void msg.success(t("addMenuItem.successAnother"));
      form.resetFields();
      // Start the next item clean: the previous item's option groups (React state, not form fields) must not linger — a leftover
      // customised set made the auto-populate effect treat the screen as "already edited" and skip the category's preset add-ons.
      setGroups([]);
      setCustEnabled(false);
      autoPopulatedSignatureRef.current = null;
      // Same default as a fresh page: first category preselected, which then loads that category's preset groups.
      form.setFieldsValue({ is_available: true, is_featured: false, sort_order: 0, category_ids: categories[0] ? [categories[0].id] : [] });
      // keep the stall selection
      setSelectedStallId(selectedStallId);
    } catch {
      void msg.error(t("addMenuItem.failedCreate"));
    }
  };

  return (
    <AdminShell>
      {/* Presentation classes keep the editor aligned with the shared shell. */}
      <div className="admin-full-bleed admin-menu-editor" style={{ margin: -24, display: "flex", flexDirection: "column", height: "calc(100vh - 64px)" }}>

        {/* ── Page header bar ── */}
        <div className="admin-editor-header" style={{
          height: 56, padding: "0 24px",
          display: "flex", alignItems: "center", gap: 16,
          background: "#fff", borderBottom: "1px solid #E5E7EB", flexShrink: 0,
        }}>
          <Button
            type="text"
            aria-label={t("common.back")}
            icon={<LeftOutlined />}
            onClick={() => navigate(-1)}
            style={{ padding: "0 8px", fontWeight: 600, color: "#616161" }}
          />
          <div className="admin-editor-header-divider" style={{ width: 1, height: 20, background: "#E5E7EB" }} />
          <h1 className="admin-editor-page-title">{isEdit ? t("addMenuItem.titlePageEdit") : t("addMenuItem.titlePage")}</h1>
        </div>

        {/* ── Body: form + preview ── */}
        {/* Below 768px this stacks into a single vertically-scrolling column
            instead of a fixed-width side-by-side split, so the form and the
            preview panel are both fully reachable on a phone. */}
        <div className="admin-editor-body max-[768px]:!flex-col max-[768px]:!overflow-y-auto" style={{ flex: 1, display: "flex", gap: 24, overflow: "hidden" }}>

          {/* Left – Form content */}
          <div className="admin-editor-form max-[768px]:!overflow-visible max-[768px]:!px-4 max-[768px]:!py-5" style={{ flex: 1, overflowY: "auto", padding: "28px 32px", background: "#F9FAFB" }}>
            <Form
              form={form}
              layout="vertical"
              initialValues={editFormInitialValues}
              onFinish={() => void handleSave()}
            >

              {/* ── 01  Basic Information ── */}
              <div className="admin-editor-section">
                <SectionHead num="01" label={t("addMenuItem.sectionBasic")} />

                <Form.Item
                  name="name"
                  label={<span style={{ fontWeight: 600, fontSize: 13, color: "#212121" }}>{t("addMenuItem.itemName")}</span>}
                  rules={[{ required: true, message: t("addMenuItem.itemNameRequired") }]}
                  style={{ marginBottom: 20 }}
                >
                  <Input
                    placeholder={t("addMenuItem.namePlaceholder")}
                    maxLength={120}
                    style={{ height: 44, borderRadius: 10, fontSize: 14 }}
                  />
                </Form.Item>

                <div className="admin-editor-basic-grid grid-cols-1 sm:grid-cols-2" style={{ display: "grid", gap: 16, marginBottom: selectedStall && !selectedStall.is_open ? 16 : 20 }}>
                  {/* Stall selector */}
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#212121", marginBottom: 8, display: "flex", alignItems: "center", gap: 8 }}>
                      {t("addMenuItem.stall")}
                      <Tag style={{ background: "#FEE2E2", color: "#DC2626", border: "none", borderRadius: 6, fontSize: 11, fontWeight: 700, padding: "0 8px", lineHeight: "20px" }}>
                        {t("addMenuItem.tagRequired")}
                      </Tag>
                    </div>
                    <Select
                      value={selectedStallId}
                      placeholder={t("addMenuItem.selectStallPh")}
                      style={{ width: "100%", height: 44 }}
                      options={stalls.map(s => ({ value: s.id, label: s.name }))}
                      onChange={(v: string) => {
                        setSelectedStallId(v);
                        form.setFieldsValue({ category_ids: [] });
                      }}
                    />
                  </div>

                  {/* Category */}
                  <Form.Item
                    name="category_ids"
                    label={<span style={{ fontWeight: 600, fontSize: 13, color: "#212121" }}>{t("addMenuItem.category")}</span>}
                    style={{ marginBottom: 0 }}
                  >
                    <Select
                      mode="multiple"
                      allowClear
                      placeholder={t("addMenuItem.selectCategoryPh")}
                      style={{ width: "100%", minHeight: 44 }}
                      disabled={!selectedStallId}
                      options={categories.map(c => ({ value: c.id, label: `${c.emoji ? c.emoji + " " : ""}${c.name}` }))}
                    />
                  </Form.Item>
                </div>

                {/* Stall paused warning */}
                {selectedStall && !selectedStall.is_open && (
                  <div style={{
                    display: "flex", alignItems: "flex-start", gap: 10,
                    background: "#FEF2F2", borderRadius: 10, padding: "12px 16px", marginBottom: 20,
                  }}>
                    <span style={{ color: "#DC2626", fontSize: 16, flexShrink: 0, marginTop: 1 }}>⚠</span>
                    <span style={{ fontSize: 13, color: "#DC2626", lineHeight: 1.5 }}>
                      {t("addMenuItem.stallPaused", { name: selectedStall.name })}
                    </span>
                  </div>
                )}

                <Form.Item
                  name="description"
                  label={<span style={{ fontWeight: 600, fontSize: 13, color: "#212121" }}>{t("addMenuItem.description")}</span>}
                  style={{ marginBottom: 0 }}
                >
                  <Input.TextArea
                    placeholder={t("addMenuItem.descPlaceholder")}
                    rows={3}
                    maxLength={400}
                    style={{ borderRadius: 10, fontSize: 14, resize: "none" }}
                  />
                </Form.Item>
              </div>

              {/* ── 02  Pricing & Availability ── */}
              <div className="admin-editor-section">
                <SectionHead num="02" label={t("addMenuItem.sectionPricing")} />

                <div className="admin-editor-pricing-grid" style={{ display: "flex", alignItems: "flex-start", gap: 24 }}>
                  {/* Price */}
                  <div style={{ flex: 1 }}>
                    <div className="admin-editor-field-label" style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.8, color: "#9E9E9E", textTransform: "uppercase", marginBottom: 8 }}>
                      {t("addMenuItem.basePrice")}
                    </div>
                    <Form.Item name="price" rules={[{ required: true, message: t("addMenuItem.priceRequired") }]} style={{ marginBottom: 0 }}>
                      <InputNumber
                        prefix={<span style={{ fontWeight: 700, color: "#9E9E9E" }}>RM</span>}
                        min={0}
                        precision={2}
                        placeholder="0.00"
                        style={{ width: "100%", height: 48, borderRadius: 10 }}
                      />
                    </Form.Item>
                  </div>

                  {/* Available for Order toggle */}
                  <div className="admin-editor-availability" style={{
                    flex: 1,
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    background: "#F9FAFB", borderRadius: 12, padding: "16px 20px",
                    marginTop: 20,
                  }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14, color: "#212121" }}>{t("addMenuItem.availableForOrder")}</div>
                      <div style={{ fontSize: 12, color: "#9E9E9E", marginTop: 2 }}>{t("addMenuItem.availableHint")}</div>
                    </div>
                    <Form.Item name="is_available" valuePropName="checked" noStyle>
                      <Switch style={{ background: isAvailable !== false ? "#16A34A" : undefined }} />
                    </Form.Item>
                  </div>

                  {/* Pin to top toggle */}
                  <div className="admin-editor-pin" style={{
                    flex: 1,
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    background: "#F9FAFB", borderRadius: 12, padding: "16px 20px",
                    marginTop: 12,
                  }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14, color: "#212121" }}>{t("addMenuItem.pinToTop")}</div>
                      <div style={{ fontSize: 12, color: "#9E9E9E", marginTop: 2 }}>{t("addMenuItem.pinToTopHint")}</div>
                    </div>
                    <Form.Item name="is_featured" valuePropName="checked" noStyle>
                      <Switch style={{ background: isFeatured ? "#16A34A" : undefined }} />
                    </Form.Item>
                  </div>
                </div>

                {/* Advanced options (collapsed) */}
                <Collapse
                  ghost
                  style={{ marginTop: 16 }}
                  items={[{
                    key: "advanced",
                    label: <span className="admin-editor-advanced-label" style={{ fontSize: 13, color: "#9E9E9E", fontWeight: 600 }}>{t("addMenuItem.advancedOptions")}</span>,
                    children: (
                      <div className="admin-editor-advanced-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                        <Form.Item name="offer_price" label={<span style={{ fontSize: 12, color: "#616161" }}>{t("addMenuItem.offerPrice")}</span>} style={{ marginBottom: 0 }}>
                          <InputNumber prefix="RM" min={0} precision={2} style={{ width: "100%" }} />
                        </Form.Item>
                        <Form.Item name="daily_stock" label={<span style={{ fontSize: 12, color: "#616161" }}>{t("addMenuItem.dailyStock")}</span>} style={{ marginBottom: 0 }}>
                          <InputNumber min={1} style={{ width: "100%" }} placeholder={t("addMenuItem.dailyStockPh")} />
                        </Form.Item>
                        <Form.Item name="sort_order" label={<span style={{ fontSize: 12, color: "#616161" }}>{t("addMenuItem.sortOrder")}</span>} style={{ marginBottom: 0 }}>
                          <InputNumber min={0} style={{ width: "100%" }} />
                        </Form.Item>
                        <Form.Item name="available_from" label={<span style={{ fontSize: 12, color: "#616161" }}>{t("addMenuItem.availableFrom")}</span>} style={{ marginBottom: 0 }}>
                          <Input placeholder={t("addMenuItem.timePh")} maxLength={5} />
                        </Form.Item>
                        <Form.Item name="available_to" label={<span style={{ fontSize: 12, color: "#616161" }}>{t("addMenuItem.availableUntil")}</span>} style={{ marginBottom: 0 }}>
                          <Input placeholder={t("addMenuItem.timePhEnd")} maxLength={5} />
                        </Form.Item>
                        <Form.Item name="offer_from" label={<span style={{ fontSize: 12, color: "#616161" }}>{t("addMenuItem.offerFrom")}</span>} style={{ marginBottom: 0 }}>
                          <Input placeholder={t("addMenuItem.timePhOffer")} maxLength={5} />
                        </Form.Item>
                      </div>
                    ),
                  }]}
                />
              </div>

              {/* ── 03  Item Image ── */}
              <div className="admin-editor-section">
                <SectionHead num="03" label={t("addMenuItem.sectionImage")} />
                <div className="admin-editor-images" style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "#616161", marginBottom: 6 }}>{t("imageUpload.squareLabel")}</div>
                    <Form.Item name="image_url" style={{ marginBottom: 0 }}>
                      <ImageUpload folder="menu" hint={t("imageUpload.menuItemHint")} />
                    </Form.Item>
                  </div>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "#616161", marginBottom: 6 }}>{t("imageUpload.wideLabel")}</div>
                    <Form.Item name="image_url_wide" style={{ marginBottom: 0 }}>
                      <ImageUpload folder="menu" hint={t("imageUpload.wideHint")} />
                    </Form.Item>
                  </div>
                </div>
                {/* Upload hint when no custom uploader is shown */}
                {!previewImage && (
                  <div style={{
                    marginTop: 8,
                    border: "2px dashed #E5E7EB", borderRadius: 12,
                    padding: "40px 24px", textAlign: "center",
                    background: "#F9FAFB", cursor: "pointer",
                    display: "none", // hidden – ImageUpload component handles its own UI
                  }}>
                    <CloudUploadOutlined style={{ fontSize: 36, color: "#F97316", marginBottom: 12 }} />
                    <div style={{ fontWeight: 700, fontSize: 14, color: "#212121", marginBottom: 4 }}>
                      {t("imageUpload.clickOrDrag")}
                    </div>
                    <div style={{ fontSize: 12, color: "#9E9E9E" }}>
                      {t("imageUpload.hintFormats")}
                    </div>
                  </div>
                )}
              </div>

              {/* ── 04  Customization Engine ── */}
              <div className="admin-editor-section">
                {/* Section header */}
                <div className="admin-customization-heading" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
                  <div className="admin-customization-title" style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span className="admin-editor-section-number" style={{
                      width: 26, height: 26, borderRadius: "50%",
                      background: "#F97316", color: "#fff",
                      fontSize: 12, fontWeight: 800,
                      display: "inline-flex", alignItems: "center", justifyContent: "center",
                    }}>04</span>
                    <h2 className="admin-editor-section-title">{t("addMenuItem.sectionCustomization")}</h2>
                  </div>
                  <div className="admin-customization-toggle" style={{
                    display: "flex", alignItems: "center", gap: 10,
                    background: "#F3F4F6", borderRadius: 20, padding: "6px 14px",
                  }}>
                    <span style={{ fontSize: 13, color: "#212121", fontWeight: 600 }}>{t("addMenuItem.enableCustomization")}</span>
                    <Switch
                      checked={custEnabled}
                      onChange={v => { setCustEnabled(v); if (!v) setGroups([]); }}
                      style={{ background: custEnabled ? "#F97316" : undefined }}
                    />
                  </div>
                </div>

                {!custEnabled ? (
                  /* Toggle OFF — prompt to enable */
                  <div className="admin-editor-customization-empty" style={{
                    border: "1.5px dashed #E5E7EB", borderRadius: 12,
                    padding: "36px 24px", textAlign: "center", background: "#F9FAFB",
                  }}>
                    <div className="admin-editor-empty-icon" aria-hidden="true"><SlidersHorizontal size={24} strokeWidth={1.75} /></div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "#212121", marginBottom: 6 }}>
                      {t("addMenuItem.custPromptTitle")}
                    </div>
                    <div style={{ fontSize: 13, color: "#9E9E9E", lineHeight: 1.6, maxWidth: 300, margin: "0 auto" }}>
                      {t("addMenuItem.custPromptBody")}
                    </div>
                  </div>
                ) : groups.length === 0 ? (
                  /* Toggle ON, no groups yet */
                  <div>
                    <div style={{ textAlign: "center", padding: "12px 0 20px", fontSize: 13, color: "#616161" }}>
                      {t("addMenuItem.noGroupsYet")}
                    </div>
                    {pickExistingNode}
                    <button
                      className="admin-editor-add-group"
                      type="button" onClick={addGroup}
                      style={{
                        width: "100%", height: 52,
                        border: "1.5px dashed #D1D5DB", borderRadius: 12,
                        background: "#fff", cursor: "pointer",
                        display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                        fontSize: 14, fontWeight: 600, color: "#212121",
                      }}
                    >
                      <PlusOutlined style={{ fontSize: 16 }} /> {t("addMenuItem.addOptionGroup")}
                    </button>
                  </div>
                ) : (
                  /* Toggle ON, with groups */
                  <div>
                    {groups.map((group) => (
                      <div
                        key={group.localId}
                        className="admin-editor-option-group"
                        style={{
                          border: "1px solid #E5E7EB", borderRadius: 12,
                          padding: "20px 20px 16px", marginBottom: 16,
                          background: "#FAFAFA",
                        }}
                      >
                        {/* ICON + GROUP NAME */}
                        <div className="admin-editor-group-heading" style={{ display: "flex", gap: 12, alignItems: "flex-start", marginBottom: 16 }}>
                          {/* Preset icon picker */}
                          <div style={{ flexShrink: 0 }}>
                            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1, color: "#9E9E9E", textTransform: "uppercase" as const, marginBottom: 8 }}>
                              {t("addMenuItem.icon")}
                            </div>
                            <div style={{ display: "flex", gap: 6 }}>
                              {GROUP_ICON_DEFS.map(icon => {
                                const active = group.icon_key === icon.key;
                                return (
                                  <button
                                    key={icon.key}
                                    type="button"
                                    title={(() => {
                                      const lk = GROUP_ICON_LABEL_KEY[icon.key];
                                      return lk ? t(lk) : icon.key;
                                    })()}
                                    onClick={() => updateGroup(group.localId, { icon_key: active ? undefined : icon.key })}
                                    style={{
                                      width: 44, height: 44, borderRadius: 10, cursor: "pointer",
                                      border: active ? "2px solid #F97316" : "1.5px solid #E5E7EB",
                                      background: active ? "#FFF7ED" : "#F9FAFB",
                                      color: active ? "#F97316" : "#9E9E9E",
                                      display: "flex", alignItems: "center", justifyContent: "center",
                                    }}
                                  >
                                    <GroupIcon iconKey={icon.key} size={18} color={active ? "#F97316" : "#9E9E9E"} />
                                  </button>
                                );
                              })}
                            </div>
                          </div>

                          {/* Group name input */}
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1, color: "#9E9E9E", textTransform: "uppercase" as const, marginBottom: 8 }}>
                              {t("addMenuItem.groupName")}
                            </div>
                            <Input
                              value={group.name}
                              placeholder={t("addMenuItem.groupNamePh")}
                              onChange={e => updateGroup(group.localId, { name: e.target.value })}
                              style={{ height: 44, borderRadius: 10, fontSize: 14, background: "#F9FAFB" }}
                            />
                          </div>
                        </div>

                        {/* REQUIRED / SELECTION / MIN / MAX */}
                        <div className="admin-editor-group-rules" style={{ display: "flex", alignItems: "center", gap: 20, marginBottom: 16, flexWrap: "wrap" as const }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1, color: "#9E9E9E", textTransform: "uppercase" as const }}>{t("addMenuItem.fieldRequired")}</div>
                            <Switch
                              size="small"
                              checked={group.required}
                              onChange={v => updateGroup(group.localId, { required: v, min_select: v ? 1 : 0 })}
                              style={{ background: group.required ? "#16A34A" : undefined }}
                            />
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1, color: "#9E9E9E", textTransform: "uppercase" as const }}>{t("addMenuItem.selection")}</div>
                            <Select
                              value={group.selection}
                              size="small" style={{ width: 148 }}
                              options={selectionOptions}
                              onChange={(v: "single" | "multiple") => updateGroup(group.localId, {
                                selection: v,
                                max_select: v === "single" ? 1 : (group.choices.length || 1),
                              })}
                            />
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1, color: "#9E9E9E", textTransform: "uppercase" as const }}>{t("addMenuItem.min")}</div>
                            <InputNumber
                              size="small" value={group.min_select} min={group.required ? 1 : 0} style={{ width: 56 }}
                              onChange={v => updateGroup(group.localId, { min_select: effectiveMin(group.required, v ?? 0) })}
                            />
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1, color: "#9E9E9E", textTransform: "uppercase" as const }}>{t("addMenuItem.max")}</div>
                            <InputNumber
                              size="small" value={group.max_select} min={1} style={{ width: 56 }}
                              onChange={v => updateGroup(group.localId, { max_select: v ?? 1 })}
                            />
                          </div>
                          <Button
                            className="admin-editor-remove-group"
                            type="text" danger size="small" icon={<DeleteOutlined />}
                            onClick={() => removeGroup(group.localId)}
                            style={{ marginLeft: "auto", color: "#9E9E9E", fontSize: 12 }}
                          >
                            {t("addMenuItem.removeGroup")}
                          </Button>
                        </div>

                        {/* Options table — horizontally scrollable below 768px
                            since the 5 fixed-width columns can't meaningfully
                            shrink onto a phone screen. */}
                        {group.choices.length > 0 && (
                          <div className="admin-options-scroll max-[768px]:!overflow-x-auto" style={{ marginBottom: 12 }}>
                            {/* Header */}
                            <div style={{
                              display: "grid", gridTemplateColumns: "1fr 120px 110px 60px 32px",
                              gap: 8, marginBottom: 8, padding: "0 4px", minWidth: 480,
                            }}>
                              {[t("addMenuItem.colOptionName"), t("addMenuItem.colPriceAdj"), t("addMenuItem.colType"), t("addMenuItem.colDefault"), ""].map((h, hi) => (
                                <div key={hi} style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1, color: "#9E9E9E", textTransform: "uppercase" as const }}>{h}</div>
                              ))}
                            </div>
                            {/* Rows */}
                            {group.choices.map(choice => (
                              <div
                                key={choice.localId}
                                style={{
                                  display: "grid", gridTemplateColumns: "1fr 120px 110px 60px 32px",
                                  gap: 8, alignItems: "center",
                                  padding: "8px 4px", borderTop: "1px solid #F3F4F6", minWidth: 480,
                                }}
                              >
                                <Input
                                  size="small" value={choice.label} placeholder={t("addMenuItem.optionNamePh")}
                                  onChange={e => updateChoice(group.localId, choice.localId, { label: e.target.value })}
                                  style={{ borderRadius: 8 }}
                                />
                                <InputNumber
                                  size="small" value={choice.extra_charge} min={0} precision={2}
                                  formatter={v => `+${v ?? "0"}`}
                                  style={{ width: "100%", borderRadius: 8 }}
                                  onChange={v => updateChoice(group.localId, choice.localId, { extra_charge: v ?? 0 })}
                                />
                                <Select
                                  size="small"
                                  value={choice.input_type}
                                  style={{ width: "100%" }}
                                  onChange={(v: "radio" | "quantity" | "input" | "textarea") => updateChoice(group.localId, choice.localId, { input_type: v })}
                                  options={inputTypeOptions}
                                />
                                <div style={{ display: "flex", justifyContent: "center" }}>
                                  <Checkbox
                                    checked={choice.isDefault}
                                    onChange={e => updateChoice(group.localId, choice.localId, { isDefault: e.target.checked })}
                                  />
                                </div>
                                <Button
                                  aria-label={t("common.delete")}
                                  type="text" size="small" icon={<DeleteOutlined />}
                                  onClick={() => removeChoice(group.localId, choice.localId)}
                                  style={{ color: "#9E9E9E", padding: 0 }}
                                />
                              </div>
                            ))}
                          </div>
                        )}

                        {/* + Add option */}
                        <Button
                          className="admin-editor-add-option"
                          type="link" icon={<PlusOutlined />}
                          onClick={() => addChoice(group.localId)}
                          style={{ padding: 0, color: "#F97316", fontWeight: 600, fontSize: 13 }}
                        >
                          {t("addMenuItem.addOption")}
                        </Button>
                      </div>
                    ))}

                    {/* Add Option Group */}
                    {pickExistingNode}
                    <button
                      className="admin-editor-add-group"
                      type="button" onClick={addGroup}
                      style={{
                        width: "100%", height: 52,
                        border: "1.5px dashed #D1D5DB", borderRadius: 12,
                        background: "#fff", cursor: "pointer",
                        display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                        fontSize: 14, fontWeight: 600, color: "#212121",
                      }}
                    >
                      <PlusOutlined style={{ fontSize: 16 }} /> {t("addMenuItem.addOptionGroup")}
                    </button>
                  </div>
                )}
              </div>

            </Form>
          </div>

          {/* Right – Customer Preview */}
          <div className="admin-editor-preview max-[768px]:!w-full max-[768px]:!border-t max-[768px]:!border-t-[#E5E7EB] max-[768px]:!overflow-visible" style={{
            width: 280, flexShrink: 0,
            background: "#fff", padding: "24px 20px 24px 0",
            overflowY: "auto",
          }}>
            <div className="admin-editor-preview-heading" style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1, color: "#9E9E9E", textTransform: "uppercase", marginBottom: 16 }}>
              {t("addMenuItem.customerPreview")}
            </div>

            {!selectedStallId && (
              <div style={{ textAlign: "center", color: "#9E9E9E", fontSize: 13, padding: "20px 0 16px" }}>
                {t("addMenuItem.selectStallPreview")}
              </div>
            )}

            <PreviewCard
              name={previewName}
              price={previewPrice}
              imageUrl={previewImage}
              isAvailable={isAvailable}
              description={previewDescription}
            />

            {!previewName && (
              <div className="admin-editor-preview-hint" style={{ marginTop: 16, fontSize: 12, color: "#9E9E9E", textAlign: "center", lineHeight: 1.6 }}>
                <span style={{ fontWeight: 600, color: "#212121" }}>{t("addMenuItem.previewNameFallback")}</span>
                <br />
                {t("addMenuItem.previewDescHint")}
              </div>
            )}
          </div>
        </div>

        {/* ── Footer bar ── */}
        <div className="admin-editor-footer max-[640px]:!h-auto max-[640px]:!flex-col max-[640px]:!items-stretch max-[640px]:!gap-2.5 max-[640px]:!py-3" style={{
          height: 64, padding: "0 24px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          background: "#fff", borderTop: "1px solid #E5E7EB", flexShrink: 0,
        }}>
          {/* Mandatory fields indicator */}
          <div className="admin-editor-completion" data-complete={mandatoryComplete} style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <CheckCircleFilled style={{ fontSize: 18, color: mandatoryComplete ? "#16A34A" : "#D1D5DB" }} />
            <span style={{ fontSize: 13, color: mandatoryComplete ? "#166534" : "#9E9E9E", fontWeight: 600 }}>
              {mandatoryComplete ? t("addMenuItem.mandatoryComplete") : t("addMenuItem.mandatoryIncomplete")}
            </span>
          </div>

          <div className="admin-editor-save-actions max-[640px]:!w-full" style={{ display: "flex", gap: 12 }}>
            {!isEdit && (
              <Button
                className="max-[640px]:!flex-1"
                style={{ height: 44, borderRadius: 8, fontWeight: 600, minWidth: 160 }}
                onClick={() => void handleSaveAndAddAnother()}
                loading={createItem.isPending}
                disabled={!mandatoryComplete}
              >
                {t("addMenuItem.saveAddAnother")}
              </Button>
            )}
            <Button
              type="primary"
              className="max-[640px]:!flex-1"
              style={{ height: 44, borderRadius: 8, fontWeight: 700, background: "#F97316", border: "none", minWidth: 120 }}
              onClick={() => void handleSave()}
              loading={createItem.isPending || updateItem.isPending}
              disabled={!selectedStallId}
            >
              {t("addMenuItem.saveItem")}
            </Button>
          </div>
        </div>

      </div>
    </AdminShell>
  );
}
